import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import CustomerProfilePanel from '../components/CustomerProfilePanel';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { API, inr } from '../lib/api';
import { wishlist, recent } from '../lib/storage';
import { Heart, Clock, MessageSquare, ClipboardList, LogOut, Phone, User } from 'lucide-react';

const AccountPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [wishProducts, setWishProducts] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [tab, setTab] = useState('profile');

  useEffect(() => {
    const t = new URLSearchParams(location.search).get('tab');
    if (t && ['profile', 'enquiries', 'requirements', 'wishlist', 'recent'].includes(t)) setTab(t);
  }, [location.search]);

  useEffect(() => {
    // Auth is now via httpOnly `tgm_customer_jwt` cookie. We must not gate this
    // page on a legacy localStorage token — new logins do not write one. Instead
    // attempt /auth/me with cookie credentials and redirect only if that 401s.
    const legacyToken = localStorage.getItem('tgmart_token');
    const h = { withCredentials: true, headers: legacyToken ? { Authorization: `Bearer ${legacyToken}` } : {} };
    axios.get(`${API}/auth/me`, h).then((r) => {
      setUser(r.data);
      localStorage.setItem('tgmart_user', JSON.stringify(r.data));
    }).catch((err) => {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate('/login');
        return;
      }
      const stored = localStorage.getItem('tgmart_user');
      if (stored) setUser(JSON.parse(stored));
    });
    axios.get(`${API}/customer/me/enquiries`, h).then((r) => setEnquiries(r.data || [])).catch(() => {});
    axios.get(`${API}/customer/me/requirements`, h).then((r) => setRequirements(r.data || [])).catch(() => {});
    const w = wishlist.list();
    if (w.length) axios.post(`${API}/products/batch`, w).then((r) => setWishProducts(r.data || [])).catch(() => {});
    const rec = recent.list();
    if (rec.length) axios.post(`${API}/products/batch`, rec).then((r) => setRecentProducts(r.data || [])).catch(() => {});
  }, [navigate]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Non-fatal: clearing local cookies + redirect below still logs the user out.
      console.warn('Backend logout call failed (continuing client-side logout)', e);
    }
    localStorage.removeItem('tgmart_token');
    localStorage.removeItem('tgmart_user');
    window.dispatchEvent(new Event('tgmart-session-change'));
    navigate('/');
  };

  const onAccountDeleted = () => {
    localStorage.removeItem('tgmart_token');
    localStorage.removeItem('tgmart_user');
    window.dispatchEvent(new Event('tgmart-session-change'));
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <section className="bg-gradient-to-r from-[#2c54b2] to-[#1e3a8a] text-white">
        <div className="max-w-7xl mx-auto px-4 py-5 sm:py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold">My Account</h1>
            <p className="text-sm text-blue-100 truncate">{user?.name} • +91 {user?.mobile}</p>
          </div>
          <div className="flex items-center gap-2 sm:shrink-0">
            <Link to="/account/messages" data-testid="account-messages-link" className="flex-1 sm:flex-initial">
              <Button size="sm" variant="outline" className="w-full sm:w-auto bg-white/10 hover:bg-white/20 border-white/30 text-white"><MessageSquare className="w-4 h-4 mr-2" /> Messages</Button>
            </Link>
            <Button onClick={logout} size="sm" variant="outline" className="flex-1 sm:flex-initial bg-white/10 hover:bg-white/20 border-white/30 text-white"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
          </div>
        </div>
      </section>
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border border-gray-200 p-1 h-auto flex-wrap">
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white" data-testid="account-tab-profile"><User className="w-4 h-4 mr-2" /> Profile</TabsTrigger>
            <TabsTrigger value="enquiries" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white"><MessageSquare className="w-4 h-4 mr-2" /> Enquiries ({enquiries.length})</TabsTrigger>
            <TabsTrigger value="requirements" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white"><ClipboardList className="w-4 h-4 mr-2" /> Requirements ({requirements.length})</TabsTrigger>
            <TabsTrigger value="wishlist" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white"><Heart className="w-4 h-4 mr-2" /> Wishlist ({wishProducts.length})</TabsTrigger>
            <TabsTrigger value="recent" className="data-[state=active]:bg-[#ee5e23] data-[state=active]:text-white"><Clock className="w-4 h-4 mr-2" /> Recently Viewed</TabsTrigger>
          </TabsList>
          <TabsContent value="profile" className="mt-4">
            <CustomerProfilePanel user={user} onUpdated={setUser} onDeleted={onAccountDeleted} />
          </TabsContent>
          <TabsContent value="enquiries" className="mt-4">
            <div className="bg-white border border-gray-200 rounded">
              {enquiries.length === 0 ? <div className="p-10 text-center text-gray-500">No enquiries yet.</div> : (
                <table className="w-full text-sm"><thead className="bg-gray-50"><tr>
                  <th className="px-4 py-2 text-left">Product</th><th className="px-4 py-2 text-left">Quantity</th>
                  <th className="px-4 py-2 text-left">Message</th><th className="px-4 py-2 text-left">When</th>
                </tr></thead><tbody>
                  {enquiries.map((e) => (
                    <tr key={e.id} className="border-t border-gray-100">
                      <td className="px-4 py-2 font-medium">{e.product_name || '—'}</td>
                      <td className="px-4 py-2">{e.quantity || '—'}</td>
                      <td className="px-4 py-2 text-gray-600 max-w-md truncate">{e.message || '—'}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{new Date(e.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody></table>
              )}
            </div>
          </TabsContent>
          <TabsContent value="requirements" className="mt-4">
            <div className="space-y-3" data-testid="account-requirements-list">
              {requirements.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded p-10 text-center text-gray-500">No requirements posted yet.</div>
              ) : requirements.map((r) => {
                const responses = r.responses || [];
                return (
                  <div key={r.id} className="bg-white border border-gray-200 rounded-md p-4" data-testid={`account-requirement-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-gray-900">{r.product}</h3>
                          {r.category_name && <span className="text-[11px] px-2 py-0.5 rounded-full bg-blue-50 text-[#2c54b2] border border-blue-100">{r.category_name}</span>}
                        </div>
                        <div className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-x-3">
                          {(r.quantity || r.unit) && <span>Qty: <b>{r.quantity || '—'} {r.unit}</b></span>}
                          {r.city && <span>{r.city}</span>}
                          <span className="text-xs text-gray-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500">Sent to {r.notified_count ?? (r.sent_to || []).length} vendors</span>
                    </div>

                    {responses.length === 0 ? (
                      <p className="mt-3 text-sm text-gray-400 border-t border-gray-100 pt-3">No responses yet. Verified vendors will reply here.</p>
                    ) : (
                      <div className="mt-3 border-t border-gray-100 pt-3">
                        <p className="text-xs font-semibold text-gray-700 uppercase mb-2">{responses.length} Vendor {responses.length === 1 ? 'Response' : 'Responses'}</p>
                        <div className="space-y-2">
                          {responses.map((resp, i) => (
                            <div key={resp.vendor_id || resp.vendor_slug || i} className="bg-gray-50 border border-gray-100 rounded p-3" data-testid={`requirement-response-${r.id}-${i}`}>
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <Link to={`/vendor/${resp.vendor_slug}`} className="font-medium text-[#2c54b2] hover:underline text-sm">{resp.vendor_name}</Link>
                                {resp.price && <span className="text-sm font-semibold text-[#ee5e23]">{resp.price}</span>}
                              </div>
                              {resp.message && <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{resp.message}</p>}
                              <div className="mt-2 flex items-center gap-2">
                                {resp.vendor_mobile && (
                                  <a href={`tel:+91${resp.vendor_mobile}`} data-testid={`requirement-response-call-${r.id}-${i}`}>
                                    <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"><Phone className="w-3.5 h-3.5 mr-1" /> Call +91 {resp.vendor_mobile}</Button>
                                  </a>
                                )}
                                <Link to={`/vendor/${resp.vendor_slug}`}>
                                  <Button size="sm" variant="outline" className="h-7 text-xs">View Profile</Button>
                                </Link>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </TabsContent>
          <TabsContent value="wishlist" className="mt-4">
            {wishProducts.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded py-12 text-center text-gray-500">No saved products. Tap the heart icon on any product to add.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {wishProducts.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </TabsContent>
          <TabsContent value="recent" className="mt-4">
            {recentProducts.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded py-12 text-center text-gray-500">Browse products to see them here.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {recentProducts.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default AccountPage;
